// src/components/EnhancementTestPage.js
/**
 * SommelAI Enhancement Pipeline Test Page
 *
 * This component provides an interface to test and demonstrate
 * the AI enhancement pipeline functionality.
 */

import React, { useState } from "react";
import { testEnhancementPipeline } from "../services/pipelineIntegration.js";
import "./EnhancementTestPage.css";

const EnhancementTestPage = () => {
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customBeverage, setCustomBeverage] = useState({
    name: "Château Margaux",
    winery: "Château Margaux",
    region: "Margaux, Bordeaux",
    country: "France",
    vintage: 2015,
    varietal: "Bordeaux Blend",
    price: 800,
    description: "Premier Grand Cru Classé from Margaux appellation",
  });
  const [selectedCategory, setSelectedCategory] = useState("wine");
  const [enhancementResult, setEnhancementResult] = useState(null);

  const runPipelineTest = async () => {
    setIsLoading(true);
    setTestResults(null);

    try {
      console.log("🧪 Running pipeline test...");
      const results = await testEnhancementPipeline();
      setTestResults(results);
    } catch (error) {
      setTestResults({
        success: false,
        error: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const testCustomBeverage = async () => {
    setIsLoading(true);
    setEnhancementResult(null);

    try {
      console.log("🚀 Testing custom beverage enhancement...");

      // Import the enhancement function directly for testing
      const { enhanceBeverageData } = await import(
        "../services/aiEnhancementPipeline.js"
      );

      const enhanced = await enhanceBeverageData(
        customBeverage,
        selectedCategory
      );
      setEnhancementResult(enhanced);
    } catch (error) {
      setEnhancementResult({
        error: error.message,
        enhanced: { status: "failed" },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCustomBeverage((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="enhancement-test-page">
      <div className="container">
        <header className="test-header">
          <h1>🤖 SommelAI Enhancement Pipeline</h1>
          <p className="subtitle">
            Test and demonstrate AI-powered beverage intelligence
          </p>
        </header>

        <div className="test-grid">
          {/* Quick Pipeline Test */}
          <div className="test-card">
            <h2>Quick Pipeline Test</h2>
            <p>Test the enhancement pipeline with built-in sample data</p>

            <button
              onClick={runPipelineTest}
              disabled={isLoading}
              className="test-button primary"
            >
              {isLoading ? "🔄 Testing..." : "🧪 Run Test"}
            </button>

            {testResults && (
              <div
                className={`result-display ${
                  testResults.success ? "success" : "error"
                }`}
              >
                <h3>Test Results</h3>
                {testResults.success ? (
                  <div>
                    <p>✅ Pipeline test successful!</p>
                    <div className="result-details">
                      <strong>Wine Enhancement:</strong>
                      <ul>
                        <li>Status: {testResults.results?.wine?.status}</li>
                        <li>
                          Confidence:{" "}
                          {testResults.results?.wine?.confidenceScore}
                        </li>
                        <li>
                          AI Sources:{" "}
                          {testResults.results?.wine?.aiSources?.join(", ")}
                        </li>
                      </ul>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p>❌ Test failed: {testResults.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Custom Beverage Test */}
          <div className="test-card">
            <h2>Custom Beverage Enhancement</h2>
            <p>Test enhancement with your own beverage data</p>

            <div className="custom-form">
              <div className="form-row">
                <label>Category:</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="wine">Wine</option>
                  <option value="beer">Beer</option>
                  <option value="spirit">Spirit</option>
                  <option value="cocktail">Cocktail</option>
                  <option value="sake">Sake</option>
                  <option value="coffee">Coffee</option>
                  <option value="tea">Tea</option>
                </select>
              </div>

              <div className="form-row">
                <label>Name:</label>
                <input
                  type="text"
                  value={customBeverage.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                />
              </div>

              <div className="form-row">
                <label>Producer:</label>
                <input
                  type="text"
                  value={customBeverage.winery}
                  onChange={(e) => handleInputChange("winery", e.target.value)}
                />
              </div>

              <div className="form-row">
                <label>Region:</label>
                <input
                  type="text"
                  value={customBeverage.region}
                  onChange={(e) => handleInputChange("region", e.target.value)}
                />
              </div>

              <div className="form-row">
                <label>Description:</label>
                <textarea
                  value={customBeverage.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  rows="3"
                />
              </div>
            </div>

            <button
              onClick={testCustomBeverage}
              disabled={isLoading}
              className="test-button secondary"
            >
              {isLoading ? "🔄 Enhancing..." : "✨ Enhance Beverage"}
            </button>

            {enhancementResult && (
              <div
                className={`result-display ${
                  enhancementResult.enhanced?.status === "enhanced"
                    ? "success"
                    : "partial"
                }`}
              >
                <h3>Enhancement Results</h3>
                {enhancementResult.enhanced?.status === "enhanced" && (
                  <div>
                    <p>✅ Enhancement successful!</p>
                    <div className="enhancement-summary">
                      <strong>Summary:</strong>
                      <ul>
                        <li>
                          Confidence Score:{" "}
                          {enhancementResult.enhanced.confidenceScore}
                        </li>
                        <li>
                          AI Sources:{" "}
                          {enhancementResult.enhanced.aiSources?.join(", ")}
                        </li>
                        <li>
                          Processing Time:{" "}
                          {enhancementResult.enhanced.processingTime}ms
                        </li>
                      </ul>

                      {enhancementResult.ratings && (
                        <div className="ratings-preview">
                          <strong>Professional Ratings:</strong>
                          <p>
                            Average Score:{" "}
                            {enhancementResult.ratings.averageScore}/100
                          </p>
                          <p>
                            Total Reviews:{" "}
                            {enhancementResult.ratings.totalReviews}
                          </p>
                        </div>
                      )}

                      {enhancementResult.tastingProfile && (
                        <div className="tasting-preview">
                          <strong>Tasting Profile:</strong>
                          <p>Available ✓</p>
                        </div>
                      )}

                      {enhancementResult.pairings && (
                        <div className="pairings-preview">
                          <strong>Food Pairings:</strong>
                          <p>
                            Classic:{" "}
                            {enhancementResult.pairings.classic?.length || 0}{" "}
                            recommendations
                          </p>
                          <p>
                            Creative:{" "}
                            {enhancementResult.pairings.creative?.length || 0}{" "}
                            recommendations
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {enhancementResult.enhanced?.status === "partial" && (
                  <div>
                    <p>⚠️ Partial enhancement completed</p>
                    <p>
                      Some AI services may have failed or returned incomplete
                      data.
                    </p>
                  </div>
                )}

                {enhancementResult.enhanced?.status === "failed" && (
                  <div>
                    <p>❌ Enhancement failed: {enhancementResult.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pipeline Information */}
        <div className="info-section">
          <h2>Pipeline Capabilities</h2>
          <div className="capabilities-grid">
            <div className="capability-card">
              <h3>🍷 Wine Enhancement</h3>
              <ul>
                <li>Professional ratings aggregation</li>
                <li>Detailed tasting profiles</li>
                <li>Producer & terroir information</li>
                <li>Advanced food pairings</li>
                <li>Market analysis & investment insights</li>
                <li>Service recommendations</li>
              </ul>
            </div>

            <div className="capability-card">
              <h3>🍺 Beer Enhancement</h3>
              <ul>
                <li>Style classification & analysis</li>
                <li>Brewing process insights</li>
                <li>Ingredient breakdown</li>
                <li>Tasting profile generation</li>
                <li>Food pairing recommendations</li>
                <li>Brewery information</li>
              </ul>
            </div>

            <div className="capability-card">
              <h3>🥃 Spirits & More</h3>
              <ul>
                <li>Spirit category analysis</li>
                <li>Distillation process details</li>
                <li>Cocktail recommendations</li>
                <li>Sake, coffee & tea profiles</li>
                <li>Production method insights</li>
                <li>Serving suggestions</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="technical-info">
          <h2>Technical Implementation</h2>
          <div className="tech-details">
            <p>
              <strong>AI Models:</strong> Claude 3.5 Sonnet + Gemini Pro
            </p>
            <p>
              <strong>Categories Supported:</strong> Wine, Beer, Spirits,
              Cocktails, Sake, Coffee, Tea
            </p>
            <p>
              <strong>Enhancement Types:</strong> Ratings, Tasting Notes,
              Pairings, Producer Info, Market Analysis
            </p>
            <p>
              <strong>Batch Processing:</strong> Configurable batch sizes with
              rate limiting
            </p>
            <p>
              <strong>Confidence Scoring:</strong> AI-powered confidence
              assessment for all enhancements
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancementTestPage;
